import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { FormConfig } from '../types';
import { WaitlistSuccessCard } from '../WaitlistSuccessCard';
import { EmailSignupLayoutId, SectionLayoutConfig } from '@/constants/layoutTypes';
import { leadSubmitSchema } from '@/lib/validations/lead';

interface EmailSignupSectionProps {
    primaryColor: string;
    formConfig: FormConfig;
    sizeClasses: { heading: string; subheading: string; body: string; small: string };
    onSubmit?: (data: { email: string; firstName?: string; lastName?: string; phone?: string; customAnswer?: string }) => Promise<{ success: boolean; data?: any }>;
    layout?: EmailSignupLayoutId;
    layoutConfig?: SectionLayoutConfig;
}

// Shared form state hook — includes per-field Zod validation
function useEmailForm(formConfig: FormConfig, onSubmit?: EmailSignupSectionProps['onSubmit']) {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [customAnswer, setCustomAnswer] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [successData, setSuccessData] = useState<any>(null);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        if (!onSubmit) return;

        // Build the payload (only include fields the form actually collects)
        const payload: Record<string, string | undefined> = { email };
        if (formConfig.collectFirstName) payload.firstName = firstName;
        if (formConfig.collectLastName) payload.lastName = lastName;
        if (formConfig.collectPhone) payload.phone = phone;
        if (formConfig.customQuestion) payload.customAnswer = customAnswer;

        // Validate with Zod — surfaces per-field errors inline
        const validation = leadSubmitSchema.safeParse(payload);
        if (!validation.success) {
            const flat = validation.error.flatten().fieldErrors;
            const errors: Record<string, string> = {};
            (Object.entries(flat) as [string, string[] | undefined][]).forEach(([key, msgs]) => {
                if (msgs && msgs[0]) errors[key] = msgs[0];
            });
            setFieldErrors(errors);
            return;
        }

        setSubmitting(true);
        try {
            const result = await onSubmit({
                email,
                firstName: formConfig.collectFirstName ? firstName : undefined,
                lastName: formConfig.collectLastName ? lastName : undefined,
                phone: formConfig.collectPhone ? phone : undefined,
                customAnswer: formConfig.customQuestion ? customAnswer : undefined,
            });

            if (result.success) {
                setSubmitted(true);
                setSuccessData(result.data);
                setEmail('');
                setFirstName('');
                setLastName('');
                setPhone('');
                setCustomAnswer('');
            } else {
                setError('Something went wrong. Please try again.');
            }
        } catch {
            setError('Something went wrong. Please try again.');
        }
        setSubmitting(false);
    };

    const fieldCount = 1 +
        (formConfig.collectFirstName ? 1 : 0) +
        (formConfig.collectLastName ? 1 : 0) +
        (formConfig.collectPhone ? 1 : 0) +
        (formConfig.customQuestion ? 1 : 0);

    return {
        email, setEmail,
        firstName, setFirstName,
        lastName, setLastName,
        phone, setPhone,
        customAnswer, setCustomAnswer,
        submitting,
        submitted,
        successData,
        error,
        fieldErrors,
        handleSubmit,
        fieldCount,
    };
}

// ============================================================================
// SHARED FORM FIELDS — Clean, professional, theme-aware, with per-field errors
// ============================================================================
interface FormFieldsProps {
    formConfig: FormConfig;
    email: string;
    setEmail: (v: string) => void;
    firstName: string;
    setFirstName: (v: string) => void;
    lastName: string;
    setLastName: (v: string) => void;
    phone: string;
    setPhone: (v: string) => void;
    customAnswer: string;
    setCustomAnswer: (v: string) => void;
    fieldCount: number;
    variant?: 'stacked' | 'inline' | 'compact';
    primaryColor: string;
    fieldErrors?: Record<string, string>;
}

function FormFields({
    formConfig,
    email, setEmail,
    firstName, setFirstName,
    lastName, setLastName,
    phone, setPhone,
    customAnswer, setCustomAnswer,
    fieldCount,
    variant = 'stacked',
    primaryColor,
    fieldErrors = {},
}: FormFieldsProps) {
    const focusBorderColor = `${primaryColor}B3`;
    const inputStyle = { '--focus-border': focusBorderColor } as React.CSSProperties;

    const baseInput = `w-full px-4 py-3 rounded-lg bg-white border text-gray-900 placeholder-gray-400 text-[15px] outline-none transition-colors duration-150`;

    // Returns the full class string for an input, with red border when errored
    const inputCls = (field: string, extra = '') => {
        const hasErr = !!fieldErrors[field];
        return `${baseInput} ${extra} ${hasErr
            ? 'border-red-400 hover:border-red-500 focus:border-red-500'
            : 'border-gray-200 hover:border-gray-300 focus:border-[var(--focus-border)]'}`;
    };

    // Inline error message under a field
    const FieldErr = ({ field }: { field: string }) =>
        fieldErrors[field] ? (
            <p className="text-red-500 text-[12px] mt-1 leading-tight flex items-center gap-1">
                <svg className="w-3 h-3 shrink-0" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M6 1a5 5 0 110 10A5 5 0 016 1zm0 3a.75.75 0 00-.75.75v2.5a.75.75 0 001.5 0v-2.5A.75.75 0 006 4zm0 5.5a.75.75 0 100-1.5.75.75 0 000 1.5z"/>
                </svg>
                {fieldErrors[field]}
            </p>
        ) : null;

    const useVerticalLayout = variant === 'stacked' || fieldCount > 2;

    if (variant === 'compact') {
        return (
            <div className="flex flex-col @md:flex-row gap-3 w-full" style={inputStyle}>
                {formConfig.collectFirstName && (
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            maxLength={100}
                            autoComplete="given-name"
                            className={inputCls('firstName')}
                        />
                        <FieldErr field="firstName" />
                    </div>
                )}
                <div className="flex-1">
                    <input
                        type="email"
                        required
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        maxLength={254}
                        autoComplete="email"
                        className={inputCls('email')}
                    />
                    <FieldErr field="email" />
                </div>
            </div>
        );
    }

    return (
        <div
            className={useVerticalLayout ? 'flex flex-col gap-3.5 w-full' : 'flex flex-col @sm:flex-row gap-3 w-full'}
            style={inputStyle}
        >
            {(formConfig.collectFirstName || formConfig.collectLastName) && (
                <div className={`flex flex-col @sm:flex-row gap-3 ${useVerticalLayout ? 'w-full' : 'w-full @sm:flex-1'}`}>
                    {formConfig.collectFirstName && (
                        <div className="flex-1">
                            <input
                                type="text"
                                placeholder="First Name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                maxLength={100}
                                autoComplete="given-name"
                                className={inputCls('firstName')}
                            />
                            <FieldErr field="firstName" />
                        </div>
                    )}
                    {formConfig.collectLastName && (
                        <div className="flex-1">
                            <input
                                type="text"
                                placeholder="Last Name"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                maxLength={100}
                                autoComplete="family-name"
                                className={inputCls('lastName')}
                            />
                            <FieldErr field="lastName" />
                        </div>
                    )}
                </div>
            )}

            <div className={useVerticalLayout ? 'w-full' : 'w-full @sm:flex-1'}>
                <input
                    type="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={254}
                    autoComplete="email"
                    className={inputCls('email')}
                />
                <FieldErr field="email" />
            </div>

            {formConfig.collectPhone && (
                <div className={useVerticalLayout ? 'w-full' : 'w-full @sm:flex-1'}>
                    <input
                        type="tel"
                        placeholder="Phone Number (e.g. +1-555-123-4567)"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        maxLength={25}
                        autoComplete="tel"
                        className={inputCls('phone')}
                    />
                    <FieldErr field="phone" />
                </div>
            )}

            {formConfig.customQuestion && (
                <div className={useVerticalLayout ? 'w-full' : 'w-full @sm:flex-1'}>
                    <input
                        type="text"
                        placeholder={formConfig.customQuestion}
                        value={customAnswer}
                        onChange={(e) => setCustomAnswer(e.target.value)}
                        maxLength={500}
                        className={inputCls('customAnswer')}
                    />
                    <FieldErr field="customAnswer" />
                </div>
            )}
        </div>
    );
}

// Shared submit button
function SubmitButton({
    primaryColor,
    submitting,
    text,
    variant = 'default',
}: {
    primaryColor: string;
    submitting: boolean;
    text: string;
    variant?: 'default' | 'gradient';
}) {
    const baseClass = 'w-full px-8 py-3.5 text-white font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-[15px]';

    if (variant === 'gradient') {
        return (
            <button
                type="submit"
                disabled={submitting}
                className={`${baseClass} rounded-xl hover:opacity-95 active:scale-[0.99]`}
                style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColorBrightness(primaryColor, -15)} 100%)`
                }}
            >
                {submitting ? 'Joining...' : text}
            </button>
        );
    }

    return (
        <button
            type="submit"
            disabled={submitting}
            className={`${baseClass} rounded-xl hover:opacity-95 active:scale-[0.99]`}
            style={{ backgroundColor: primaryColor }}
        >
            {submitting ? 'Joining...' : text}
        </button>
    );
}

// ============================================================================
// LAYOUT 1: CENTERED CLASSIC (Default)
// ============================================================================
function EmailSignupCentered({
    primaryColor,
    formConfig,
    sizeClasses,
    onSubmit
}: Omit<EmailSignupSectionProps, 'layout' | 'layoutConfig'>) {
    const form = useEmailForm(formConfig, onSubmit);

    return (
        <section id="emailSignup" className="py-20 px-6 bg-gray-50 @container">
            <div className="max-w-2xl mx-auto text-center">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                    {formConfig.emailPrompt || 'Join Our Waitlist'}
                </h2>
                <p className={`${sizeClasses.subheading} text-gray-500 mb-10`}>
                    Be the first to know when we launch. Get exclusive early access.
                </p>
                {form.submitted && form.successData ? (
                    <WaitlistSuccessCard
                        position={form.successData.position || 1}
                        totalWaitlist={form.successData.totalWaitlist || 1}
                        referralLink={form.successData.referralLink || ''}
                        referralsCount={form.successData.referralsCount || 0}
                        primaryColor={primaryColor}
                        successMessage={formConfig.successMessage}
                        gamificationEnabled={form.successData.gamificationEnabled}
                        milestones={form.successData.milestones}
                    />
                ) : (
                    <form onSubmit={form.handleSubmit} className="flex flex-col gap-3.5 max-w-lg mx-auto w-full">
                        <FormFields
                            formConfig={formConfig}
                            email={form.email}
                            setEmail={form.setEmail}
                            firstName={form.firstName}
                            setFirstName={form.setFirstName}
                            lastName={form.lastName}
                            setLastName={form.setLastName}
                            phone={form.phone}
                            setPhone={form.setPhone}
                            customAnswer={form.customAnswer}
                            setCustomAnswer={form.setCustomAnswer}
                            fieldCount={form.fieldCount}
                            primaryColor={primaryColor}
                            fieldErrors={form.fieldErrors}
                        />

                        {form.error && (
                            <p className="text-red-500 text-sm">{form.error}</p>
                        )}

                        <SubmitButton
                            primaryColor={primaryColor}
                            submitting={form.submitting}
                            text={formConfig.emailButtonText || 'Join Waitlist'}
                        />
                    </form>
                )}
                <p className={`${sizeClasses.small} text-gray-400 mt-4`}>
                    We respect your privacy. Unsubscribe anytime.
                </p>
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 2: SPLIT VIEW
// ============================================================================
function EmailSignupSplit({
    primaryColor,
    formConfig,
    sizeClasses,
    onSubmit,
    layoutConfig
}: Omit<EmailSignupSectionProps, 'layout'>) {
    const form = useEmailForm(formConfig, onSubmit);
    const imagePosition = layoutConfig?.imagePosition || 'left';

    const visualSection = (
        <div className="flex-1 flex items-center justify-center p-8 @3xl:p-12">
            {layoutConfig?.imageUrl ? (
                <img
                    src={layoutConfig.imageUrl}
                    alt="Join our waitlist"
                    className="w-full max-h-[400px] object-contain rounded-2xl"
                />
            ) : (
                <div
                    className="w-full aspect-square max-w-sm rounded-3xl flex flex-col items-center justify-center p-8 relative overflow-hidden"
                    style={{
                        background: `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}18 100%)`
                    }}
                >
                    <div
                        className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10"
                        style={{ backgroundColor: primaryColor }}
                    />
                    <div
                        className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-10"
                        style={{ backgroundColor: primaryColor }}
                    />

                    <div
                        className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 relative z-10"
                        style={{ backgroundColor: primaryColor }}
                    >
                        <Icon name="RocketLaunchIcon" className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-2 relative z-10">
                        Be First in Line
                    </h3>
                    <p className="text-gray-500 text-center text-sm relative z-10">
                        Join thousands of early adopters
                    </p>

                    <div className="flex items-center gap-2 mt-6 relative z-10">
                        <div className="flex -space-x-2">
                            {[1, 2, 3, 4].map((i) => (
                                <div
                                    key={i}
                                    className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-white"
                                    style={{ backgroundColor: primaryColor, opacity: 1 - (i * 0.15) }}
                                >
                                    {String.fromCharCode(64 + i)}
                                </div>
                            ))}
                        </div>
                        <span className="text-sm text-gray-500 font-medium">+2.4k joined</span>
                    </div>
                </div>
            )}
        </div>
    );

    const formSection = (
        <div className="flex-1 flex items-center justify-center p-6 @3xl:p-12">
            <div className="w-full max-w-md">
                <h2 className="text-3xl @3xl:text-4xl font-bold text-gray-900 mb-3">
                    {formConfig.emailPrompt || 'Join Our Waitlist'}
                </h2>
                <p className={`${sizeClasses.body} text-gray-500 mb-8`}>
                    Be the first to know when we launch. Get exclusive early access and special perks.
                </p>

                {form.submitted && form.successData ? (
                    <WaitlistSuccessCard
                        position={form.successData.position || 1}
                        totalWaitlist={form.successData.totalWaitlist || 1}
                        referralLink={form.successData.referralLink || ''}
                        referralsCount={form.successData.referralsCount || 0}
                        primaryColor={primaryColor}
                        successMessage={formConfig.successMessage}
                        gamificationEnabled={form.successData.gamificationEnabled}
                        milestones={form.successData.milestones}
                    />
                ) : (
                    <form onSubmit={form.handleSubmit} className="flex flex-col gap-3.5">
                        <FormFields
                            formConfig={formConfig}
                            email={form.email}
                            setEmail={form.setEmail}
                            firstName={form.firstName}
                            setFirstName={form.setFirstName}
                            lastName={form.lastName}
                            setLastName={form.setLastName}
                            phone={form.phone}
                            setPhone={form.setPhone}
                            customAnswer={form.customAnswer}
                            setCustomAnswer={form.setCustomAnswer}
                            fieldCount={form.fieldCount}
                            variant="stacked"
                            primaryColor={primaryColor}
                            fieldErrors={form.fieldErrors}
                        />

                        {form.error && (
                            <p className="text-red-500 text-sm">{form.error}</p>
                        )}

                        <SubmitButton
                            primaryColor={primaryColor}
                            submitting={form.submitting}
                            text={formConfig.emailButtonText || 'Join Waitlist'}
                        />

                        <p className={`${sizeClasses.small} text-gray-400 text-center`}>
                            We respect your privacy. Unsubscribe anytime.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );

    return (
        <section id="emailSignup" className="bg-white @container">
            <div className={`flex flex-col @3xl:flex-row min-h-[500px] ${imagePosition === 'right' ? '@3xl:flex-row-reverse' : ''}`}>
                {visualSection}
                {formSection}
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 3: MINIMAL INLINE
// ============================================================================
function EmailSignupMinimal({
    primaryColor,
    formConfig,
    sizeClasses,
    onSubmit
}: Omit<EmailSignupSectionProps, 'layout' | 'layoutConfig'>) {
    const form = useEmailForm(formConfig, onSubmit);

    return (
        <section id="emailSignup" className="py-16 px-6 bg-white border-y border-gray-100 @container">
            <div className="max-w-3xl mx-auto">
                {form.submitted && form.successData ? (
                    <div className="max-w-md mx-auto">
                        <WaitlistSuccessCard
                            position={form.successData.position || 1}
                            totalWaitlist={form.successData.totalWaitlist || 1}
                            referralLink={form.successData.referralLink || ''}
                            referralsCount={form.successData.referralsCount || 0}
                            primaryColor={primaryColor}
                            successMessage={formConfig.successMessage}
                            gamificationEnabled={form.successData.gamificationEnabled}
                            milestones={form.successData.milestones}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-6">
                        <div className="text-center">
                            <h2 className="text-2xl @md:text-3xl font-bold text-gray-900 mb-2">
                                {formConfig.emailPrompt || 'Join Our Waitlist'}
                            </h2>
                            <p className={`${sizeClasses.body} text-gray-400`}>
                                Get notified when we launch.
                            </p>
                        </div>

                        <div className="w-full max-w-xl">
                            <form onSubmit={form.handleSubmit} className="flex flex-col @md:flex-row gap-3 items-center justify-center">
                                <FormFields
                                    formConfig={formConfig}
                                    email={form.email}
                                    setEmail={form.setEmail}
                                    firstName={form.firstName}
                                    setFirstName={form.setFirstName}
                                    lastName={form.lastName}
                                    setLastName={form.setLastName}
                                    phone={form.phone}
                                    setPhone={form.setPhone}
                                    customAnswer={form.customAnswer}
                                    setCustomAnswer={form.setCustomAnswer}
                                    fieldCount={form.fieldCount}
                                    variant="compact"
                                    primaryColor={primaryColor}
                                    fieldErrors={form.fieldErrors}
                                />
                                <button
                                    type="submit"
                                    disabled={form.submitting}
                                    className="px-6 py-3.5 text-white rounded-xl font-semibold hover:opacity-95 active:scale-[0.99] transition-all whitespace-nowrap disabled:opacity-50 shrink-0 text-[15px]"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    {form.submitting ? 'Joining...' : formConfig.emailButtonText || 'Join'}
                                </button>
                            </form>
                            {form.error && (
                                <p className="text-red-500 text-sm mt-2 text-center">{form.error}</p>
                            )}
                            <p className={`${sizeClasses.small} text-gray-400 mt-3 text-center`}>
                                No spam. Unsubscribe anytime.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 4: FLOATING CARD
// ============================================================================
function EmailSignupCard({
    primaryColor,
    formConfig,
    sizeClasses,
    onSubmit
}: Omit<EmailSignupSectionProps, 'layout' | 'layoutConfig'>) {
    const form = useEmailForm(formConfig, onSubmit);

    return (
        <section
            id="emailSignup"
            className="py-20 px-6 relative overflow-hidden @container"
            style={{
                background: `linear-gradient(135deg, ${primaryColor}05 0%, ${primaryColor}10 50%, ${primaryColor}05 100%)`
            }}
        >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30">
                <div
                    className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl"
                    style={{ backgroundColor: primaryColor, opacity: 0.08 }}
                />
                <div
                    className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full blur-3xl"
                    style={{ backgroundColor: primaryColor, opacity: 0.1 }}
                />
            </div>

            <div className="max-w-xl mx-auto relative z-10">
                <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/40 border border-gray-100 p-8 @md:p-10">
                    {/* Badge */}
                    <div className="flex justify-center mb-6">
                        <span
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium text-white"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <Icon name="SparklesIcon" className="w-3.5 h-3.5" />
                            Early Access
                        </span>
                    </div>

                    <h2 className="text-3xl @md:text-4xl font-bold text-gray-900 text-center mb-3">
                        {formConfig.emailPrompt || 'Join the waitlist'}
                    </h2>
                    <p className={`${sizeClasses.body} text-gray-500 text-center mb-8 max-w-md mx-auto`}>
                        Be among the first to experience what we&apos;re building. Early members get exclusive benefits.
                    </p>

                    {form.submitted && form.successData ? (
                        <WaitlistSuccessCard
                            position={form.successData.position || 1}
                            totalWaitlist={form.successData.totalWaitlist || 1}
                            referralLink={form.successData.referralLink || ''}
                            referralsCount={0}
                            primaryColor={primaryColor}
                            successMessage={formConfig.successMessage}
                        />
                    ) : (
                        <form onSubmit={form.handleSubmit} className="flex flex-col gap-3.5">
                            <FormFields
                                formConfig={formConfig}
                                email={form.email}
                                setEmail={form.setEmail}
                                firstName={form.firstName}
                                setFirstName={form.setFirstName}
                                lastName={form.lastName}
                                setLastName={form.setLastName}
                                phone={form.phone}
                                setPhone={form.setPhone}
                                customAnswer={form.customAnswer}
                                setCustomAnswer={form.setCustomAnswer}
                                fieldCount={form.fieldCount}
                                variant="stacked"
                                primaryColor={primaryColor}
                                fieldErrors={form.fieldErrors}
                            />

                            {form.error && (
                                <p className="text-red-500 text-sm text-center">{form.error}</p>
                            )}

                            <SubmitButton
                                primaryColor={primaryColor}
                                submitting={form.submitting}
                                text={formConfig.emailButtonText || 'Get Early Access'}
                                variant="gradient"
                            />
                        </form>
                    )}

                    {/* Trust indicators */}
                    <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-gray-400">
                            <Icon name="ShieldCheckIcon" className="w-3.5 h-3.5" />
                            <span className="text-xs font-medium">No spam</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-400">
                            <Icon name="LockClosedIcon" className="w-3.5 h-3.5" />
                            <span className="text-xs font-medium">Secure</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-400">
                            <Icon name="BoltIcon" className="w-3.5 h-3.5" />
                            <span className="text-xs font-medium">Instant access</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Helper function to adjust color brightness
function adjustColorBrightness(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, (num >> 8 & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

// ============================================================================
// MAIN EXPORT WITH LAYOUT SWITCHING
// ============================================================================
export function EmailSignupSection({
    primaryColor,
    formConfig,
    sizeClasses,
    onSubmit,
    layout = 'centered',
    layoutConfig
}: EmailSignupSectionProps) {
    const commonProps = { primaryColor, formConfig, sizeClasses, onSubmit };

    switch (layout) {
        case 'split':
            return <EmailSignupSplit {...commonProps} layoutConfig={layoutConfig} />;
        case 'minimal':
            return <EmailSignupMinimal {...commonProps} />;
        case 'card':
            return <EmailSignupCard {...commonProps} />;
        case 'centered':
        default:
            return <EmailSignupCentered {...commonProps} />;
    }
}
